import { useMemo, useState } from 'react';

import { useShowMore } from 'hooks';
import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { List } from 'components/list';
import { mapQueries } from 'utils/query';
import { Drawer } from 'components/drawer';
import { Details } from 'components/details';
import { EventCheckout } from 'store/api/event';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { useEmployeeQuery } from 'store/api/analytics';
import { formatPrice, joinStrings } from 'utils/helpers';
import { CloseButton } from 'components/specified-buttons';
import { formatDate, formatTimezoneSpecific } from 'utils/date';
import { Avatar, AvatarPlaceholderType } from 'components/avatar';
import { SaleDetailsDrawer } from 'components/sale-details-drawer';
import { useGetDailyEmployeeCommissionQuery } from 'store/api/commission-level';

import * as Styles from './styles';
import { EmployeeDrawerAnalyticsProps } from './types';
import { titles, template, getInfoData } from './config';

export const EmployeeDrawerAnalytics = ({
  opened,
  onClose,
  endDate,
  employee,
  startDate,
  saleDrawerProps,
  hideEmployeeCommissions,
  ...drawerProps
}: EmployeeDrawerAnalyticsProps) => {
  const [selectedReceipt, setSelectedReceipt] = useState<EventCheckout>();

  const commonSkip = {
    skip: !opened || !employee?.userId,
  };
  const { data, ...props } = useEmployeeQuery(
    {
      id: employee?.userId || 0,
      endDate: formatDate(endDate),
      startDate: formatDate(startDate),
    },
    commonSkip
  );

  const { data: dailyCommission, ...dailyCommissionProps } =
    useGetDailyEmployeeCommissionQuery(
      { date: formatDate(startDate), id: employee?.employeeId || 0 },
      commonSkip
    );

  const infoData = useMemo(
    () =>
      getInfoData({
        data,
        hideEmployeeCommissions,
        commissionPercent: dailyCommission?.commissionLevel?.percentage,
      }),
    [
      data,
      dailyCommission?.commissionLevel?.percentage,
      hideEmployeeCommissions,
    ]
  );

  const { visibleData, ...showMoreProps } = useShowMore(data?.checkouts);

  return (
    <>
      <Drawer
        topOffset="20%"
        opened={opened && !selectedReceipt}
        {...drawerProps}
      >
        <Details {...mapQueries([props, dailyCommissionProps])}>
          <Flex gap={16} justifyContent="space-between">
            <Flex gap={16}>
              <Avatar
                square
                size={64}
                icon={<Icon.User />}
                url={data?.employee?.user?.photoUrl}
                placeholder={AvatarPlaceholderType.GreyIcon}
              />

              <Flex gap={8} flexDirection="column">
                <h2>{data?.employee?.user?.name}</h2>
                <Styles.Title>{data?.employee?.user?.role?.name}</Styles.Title>
              </Flex>
            </Flex>

            {onClose && <CloseButton onClose={onClose} />}
          </Flex>

          <Styles.Container>
            {infoData.map(({ icon, title, value, withoutFormat }, i) => (
              <Styles.StyledInfoBlock key={i}>
                <Flex gap={16} alignItems="center">
                  {icon}

                  <Flex gap={8} flexDirection="column">
                    <Styles.Title isInfo>{title}</Styles.Title>
                    <h3>{withoutFormat ? value : formatPrice(value)}</h3>
                  </Flex>
                </Flex>
              </Styles.StyledInfoBlock>
            ))}
          </Styles.Container>

          <Flex gap={16} flexDirection="column">
            <h2>Sales</h2>
            <List
              {...showMoreProps}
              count={visibleData?.length}
              header={<ListHeader titles={titles} template={template} />}
            >
              {visibleData?.map((value) => (
                <ListCard
                  key={value.id}
                  headers={titles}
                  template={template}
                  onClick={() => setSelectedReceipt(value)}
                  values={[
                    formatTimezoneSpecific(
                      value.createdAt,
                      value.event?.timezone?.code,
                      'LT'
                    ),
                    value.id,
                    joinStrings(
                      value?.commissions?.map(
                        (commission) => commission?.user?.name
                      ),
                      ', '
                    ),
                    formatPrice(value.subTotal),
                    formatPrice(value.tip),
                    formatPrice(value.processingFeesAmount),
                    formatPrice(value.taxAmount),
                    formatPrice(value.isFreeShipping ? 0 : value.shippingPrice),
                    formatPrice(value.saleTotal),
                  ]}
                />
              ))}
            </List>
          </Flex>
        </Details>
      </Drawer>

      <SaleDetailsDrawer
        {...saleDrawerProps}
        selectedReceipt={selectedReceipt}
        setSelectedReceipt={setSelectedReceipt}
      />
    </>
  );
};
